import type { ReactNode } from 'react';
import {
  CheckoutDrawer as KitCheckoutDrawer,
  type CheckoutSubmission as KitSubmission,
  type OfferCheckoutProps as KitOfferProps,
  type RequestCheckoutProps as KitRequestProps,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import type {
  CheckoutItem,
  CheckoutTravelDocs,
  ConciergeRequest,
  ConciergeSubmit,
} from '@voll-intelligence/genui-contract';
import type { GenUiAction } from '../../../../shared/ui/action.js';
import { buildConciergeSubmit } from '../../core/concierge_submit.js';
import { useCheckoutEnv } from '../../core/checkout_env.js';
import { buildMultiTravelerCheckoutSubmit } from '../../core/checkout_submit.js';

/*
 * The chat's checkout drawer (#5000): the kit's organism with the environment
 * from the `GenUiConfig` and the wire on both ends — the block's items or the
 * concierge request in, the `product-checkout-submit` action or the
 * `ConciergeSubmit` out.
 */

type CheckoutSubject =
  | { mode: 'offer'; item: CheckoutItem; items?: CheckoutItem[] }
  | { mode: 'request'; request: ConciergeRequest };

type CheckoutSubmission =
  | { mode: 'offer'; action: Extract<GenUiAction, { type: 'product-checkout-submit' }> }
  | { mode: 'request'; submit: ConciergeSubmit };

export interface OfferCheckoutProps extends Omit<KitOfferProps, 'env' | 'subject' | 'onSubmit'> {
  subject: Extract<CheckoutSubject, { mode: 'offer' }>;
  challengeId: string;
  /** Os documentos da viagem marcados no card, repassados ao envio. */
  travelDocsChecked?: readonly string[];
  /** Os documentos que o card mostra: a ciência deles vai no envio. */
  travelDocsShown?: CheckoutTravelDocs | undefined;
  onSubmit: (submission: Extract<CheckoutSubmission, { mode: 'offer' }>) => void;
}

export interface RequestCheckoutProps extends Omit<
  KitRequestProps,
  'env' | 'subject' | 'onSubmit'
> {
  subject: Extract<CheckoutSubject, { mode: 'request' }>;
  onSubmit: (submission: Extract<CheckoutSubmission, { mode: 'request' }>) => void;
}

export type CheckoutDrawerProps = OfferCheckoutProps | RequestCheckoutProps;

/** The offer's form, as the kit hands it, into the wire action. */
export function offerCheckoutAction(
  submission: Extract<KitSubmission, { mode: 'offer' }>,
  offer: {
    challengeId: string;
    item: CheckoutItem;
    travelDocsChecked?: readonly string[];
    travelDocs?: CheckoutTravelDocs | undefined;
  },
): Extract<GenUiAction, { type: 'product-checkout-submit' }> {
  return buildMultiTravelerCheckoutSubmit({
    ...submission.form,
    challengeId: offer.challengeId,
    item: offer.item,
    ...(offer.travelDocsChecked ? { travelDocsChecked: offer.travelDocsChecked } : {}),
    travelDocs: offer.travelDocs,
  });
}

/** The request's form and card, as the kit hands them, into the wire submit. */
export function conciergeCheckoutSubmit(
  submission: Extract<KitSubmission, { mode: 'request' }>,
  request: ConciergeRequest,
): ConciergeSubmit {
  return buildConciergeSubmit({ ...submission.form, request, concierge: submission.concierge });
}

// Duas assinaturas, como no kit: a unida perde o tipo contextual do `onSubmit`.
export function CheckoutDrawer(props: Readonly<OfferCheckoutProps>): ReactNode;
// eslint-disable-next-line @typescript-eslint/unified-signatures -- a assinatura unida perde o tipo contextual do `onSubmit`
export function CheckoutDrawer(props: Readonly<RequestCheckoutProps>): ReactNode;
export function CheckoutDrawer(props: Readonly<CheckoutDrawerProps>): ReactNode {
  const env = useCheckoutEnv();
  if (props.subject.mode === 'offer') {
    const { challengeId, travelDocsChecked, travelDocsShown, onSubmit, subject, ...rest } =
      props as Readonly<OfferCheckoutProps>;
    return (
      <KitCheckoutDrawer
        {...rest}
        env={env}
        subject={subject}
        onSubmit={(submission) => {
          onSubmit({
            mode: 'offer',
            action: offerCheckoutAction(submission, {
              challengeId,
              item: subject.item,
              ...(travelDocsChecked ? { travelDocsChecked } : {}),
              travelDocs: travelDocsShown,
            }),
          });
        }}
      />
    );
  }
  const { onSubmit, subject, ...rest } = props as Readonly<RequestCheckoutProps>;
  return (
    <KitCheckoutDrawer
      {...rest}
      env={env}
      subject={subject}
      onSubmit={(submission) => {
        onSubmit({ mode: 'request', submit: conciergeCheckoutSubmit(submission, subject.request) });
      }}
    />
  );
}
